import { useState, type ReactNode } from 'react';
import {
  IconActivity,
  IconAlertTriangle,
  IconCircleCheck,
  IconClock,
  IconDatabase,
  IconFolder,
  IconGitCommit,
  IconHistory,
  IconLock,
  IconServer,
  IconStack,
} from '@tabler/icons-react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { formatBytes, relativeTime } from '@/lib/utils';
import { Sparkline } from '../dam/Sparkline.js';
import {
  api,
  type AdminAction,
  type BackupSnapshot,
  type ChangeRow,
  type ContentHealthResponse,
  type DepotListResponse,
  type HealthResponse,
  type JobsSummary,
  type SeatsResponse,
  type StatusLocksResponse,
  type StorageSummary,
} from '../api.js';

// Status = the landing page for every dev. It's a triage-first ops
// overview: a "needs attention" banner up top (only surfaces problems),
// then a grid of at-a-glance cards that drill down into the full pages
// (Admin, Depot, Changes). Admin-only data (render queue / storage /
// backups) is gated on me().isAdmin — non-admins get the session-OK
// widgets and a banner scoped to what they can see.

const DISK_WARN_PCT = 75;
const DISK_CRIT_PCT = 85;
const BACKUP_STALE_HOURS = 30; // checkpoints cut ~daily; older = worth a look

export function HomePage() {
  const me = useQuery({ queryKey: ['auth', 'me'], queryFn: api.me });
  const isAdmin = me.data?.isAdmin ?? false;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      <AttentionBanner isAdmin={isAdmin} />
      <div className="flex flex-wrap items-start gap-4">
        <ServerCard />
        {isAdmin && <RenderPipelineCard />}
        {isAdmin && <StorageCard />}
        {isAdmin && <BackupCard />}
        <ContentHealthCard />
        <RecentActivityCard />
        <DepotOverviewCard />
        <LocksCard />
        {isAdmin && <AdminActionsCard />}
      </div>
    </div>
  );
}

// ---- shared query hooks (identical keys → react-query shares one fetch
// between the banner and the cards) -------------------------------------

function useHealth(): UseQueryResult<HealthResponse> {
  return useQuery({ queryKey: ['health'], queryFn: api.health, refetchInterval: 60_000 });
}
function useJobs(enabled: boolean): UseQueryResult<JobsSummary> {
  return useQuery({ queryKey: ['admin.jobs.summary'], queryFn: api.adminJobsSummary, enabled, refetchInterval: 15_000 });
}
function useStorage(enabled: boolean): UseQueryResult<StorageSummary> {
  return useQuery({ queryKey: ['admin.storage.summary'], queryFn: () => api.adminStorageSummary(), enabled, refetchInterval: 5 * 60_000 });
}
function useBackup(enabled: boolean): UseQueryResult<{ snapshot: BackupSnapshot | null; message?: string }> {
  return useQuery({ queryKey: ['admin.backups.snapshot'], queryFn: api.adminBackupSnapshot, enabled, refetchInterval: 60_000 });
}
// Session-gated (available to every dev) → safe to use unconditionally.
function useSeats(): UseQueryResult<SeatsResponse> {
  return useQuery({ queryKey: ['status.seats'], queryFn: api.statusSeats, refetchInterval: 60_000 });
}
function useContentHealth(): UseQueryResult<ContentHealthResponse> {
  return useQuery({ queryKey: ['status.contentHealth'], queryFn: api.statusContentHealth, refetchInterval: 60_000 });
}
function useLocks(): UseQueryResult<StatusLocksResponse> {
  return useQuery({ queryKey: ['status.locks'], queryFn: api.statusLocks, refetchInterval: 60_000 });
}
// Seat-pressure tone: red when at/over the cap, amber one below, else green.
function seatTone(count: number | null, limit: number | null): 'green' | 'yellow' | 'red' | 'neutral' {
  if (count === null || limit === null) return 'neutral';
  if (count >= limit) return 'red';
  if (count >= limit - 1) return 'yellow';
  return 'green';
}

function diskPct(s: StorageSummary | undefined): number | null {
  if (!s || !s.filesystem.totalBytes) return null;
  return (s.filesystem.usedBytes / s.filesystem.totalBytes) * 100;
}
function ageHours(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}
function statusTone(status: string | null | undefined): 'green' | 'red' | 'blue' | 'neutral' {
  if (status === 'success') return 'green';
  if (status === 'failed') return 'red';
  if (status === 'in-progress') return 'blue';
  return 'neutral';
}

// ---- "Needs attention" banner ------------------------------------------

type Severity = 'red' | 'amber';
interface Attn { id: string; severity: Severity; text: string; to?: string }

// Per-alert dismissal, persisted per-browser. Several signals on this
// stack are known-and-permanent (e.g. seats at the free-tier cap), so the
// banner would otherwise nag forever. Dismissing mutes by stable alert id;
// muted alerts stay listed behind a "show muted" toggle so nothing is lost.
const DISMISSED_ALERTS_KEY = 'theia.status.dismissedAlerts';

function useDismissedAlerts() {
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => {
    try {
      const raw = window.localStorage.getItem(DISMISSED_ALERTS_KEY);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set<string>();
    }
  });
  const write = (next: Set<string>) => {
    setDismissed(next);
    try {
      window.localStorage.setItem(DISMISSED_ALERTS_KEY, JSON.stringify([...next]));
    } catch {
      // private mode / quota — dismissal just won't persist across reloads
    }
  };
  return {
    dismissed,
    dismiss: (id: string) => write(new Set(dismissed).add(id)),
    restore: (id: string) => {
      const next = new Set(dismissed);
      next.delete(id);
      write(next);
    },
  };
}

function AttentionBanner({ isAdmin }: { isAdmin: boolean }) {
  const health = useHealth();
  const jobs = useJobs(isAdmin);
  const storage = useStorage(isAdmin);
  const backup = useBackup(isAdmin);
  const seats = useSeats();
  const content = useContentHealth();
  const { dismissed, dismiss, restore } = useDismissedAlerts();
  const [showMuted, setShowMuted] = useState(false);

  const items: Attn[] = [];
  if (health.data && !health.data.p4d.reachable) {
    items.push({ id: 'p4d-unreachable', severity: 'red', text: 'Perforce server unreachable' });
  }
  const u = seats.data?.users;
  if (u && u.count !== null && u.limit !== null && u.count >= u.limit) {
    items.push({ id: 'seats', severity: 'red', text: `All ${u.limit} license seats in use` });
  } else if (u && u.count !== null && u.limit !== null && u.count >= u.limit - 1) {
    items.push({ id: 'seats', severity: 'amber', text: `${u.count} of ${u.limit} license seats used` });
  }
  if (content.data && content.data.brokenRefs > 0) {
    items.push({ id: 'broken-refs', severity: 'amber', text: `${content.data.brokenRefs} broken reference(s)`, to: '/depot' });
  }
  if (jobs.data) {
    if (jobs.data.counts.failed > 0) {
      items.push({ id: 'render-failed', severity: 'red', text: `${jobs.data.counts.failed} render job(s) failed`, to: '/admin' });
    }
    if (jobs.data.staleCount > 0) {
      items.push({ id: 'pending-rerender', severity: 'amber', text: `${jobs.data.staleCount.toLocaleString()} asset(s) pending re-render`, to: '/admin' });
    }
  }
  const pct = diskPct(storage.data);
  if (pct !== null && pct >= DISK_WARN_PCT) {
    items.push({ id: 'disk', severity: pct >= DISK_CRIT_PCT ? 'red' : 'amber', text: `Disk ${pct.toFixed(0)}% full`, to: '/admin' });
  }
  const snap = backup.data?.snapshot;
  if (snap) {
    if (snap.lastRunStatus === 'failed') {
      items.push({ id: 'backup-failed', severity: 'red', text: 'Last backup run failed', to: '/admin/backups' });
    } else {
      const age = ageHours(snap.lastRunEndedAt ?? snap.takenAt);
      if (age !== null && age > BACKUP_STALE_HOURS) {
        items.push({ id: 'backup-stale', severity: 'amber', text: `Backup stale (${age.toFixed(0)}h)`, to: '/admin/backups' });
      }
    }
  }

  const active = items.filter((i) => !dismissed.has(i.id));
  const muted = items.filter((i) => dismissed.has(i.id));
  const stillChecking =
    health.isLoading || (isAdmin && (jobs.isLoading || storage.isLoading || backup.isLoading));

  const mutedToggle = muted.length > 0 && (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setShowMuted((v) => !v)}
        className="text-[11px] text-muted-foreground hover:text-foreground"
      >
        {showMuted ? 'Hide' : 'Show'} {muted.length} muted
      </button>
      {showMuted && (
        <ul className="mt-1 flex flex-col gap-1">
          {muted.map((it) => (
            <li key={it.id} className="flex items-center gap-2 text-muted-foreground">
              <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-600" />
              <span className="line-through">{it.text}</span>
              <button
                type="button"
                onClick={() => restore(it.id)}
                className="text-[11px] text-theia-400 hover:underline"
              >
                restore
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  if (active.length === 0) {
    return (
      <Alert tone="info">
        <IconCircleCheck />
        <AlertTitle>All systems nominal</AlertTitle>
        <AlertDescription>
          {stillChecking
            ? 'Still checking a few signals…'
            : muted.length > 0
              ? 'No active issues — some alerts are muted.'
              : 'No issues need attention right now.'}
          {mutedToggle}
        </AlertDescription>
      </Alert>
    );
  }

  const hasRed = active.some((i) => i.severity === 'red');
  return (
    <Alert tone={hasRed ? 'destructive' : 'warning'}>
      <IconAlertTriangle />
      <AlertTitle>Needs attention ({active.length})</AlertTitle>
      <AlertDescription>
        <ul className="mt-1 flex flex-col gap-1">
          {active.map((it) => (
            <li key={it.id} className="flex items-center gap-2">
              <span
                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: it.severity === 'red' ? '#f85149' : '#d29922' }}
              />
              {it.to ? (
                <Link to={it.to} className="hover:underline">{it.text}</Link>
              ) : (
                <span>{it.text}</span>
              )}
              <button
                type="button"
                onClick={() => dismiss(it.id)}
                aria-label={`Mute alert: ${it.text}`}
                title="Mute this alert"
                className="ml-auto rounded px-1 leading-none text-muted-foreground hover:bg-neutral-800 hover:text-foreground"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
        {mutedToggle}
      </AlertDescription>
    </Alert>
  );
}

// ---- card chrome --------------------------------------------------------

function StatCard({
  title,
  icon,
  to,
  children,
  minW = 260,
}: {
  title: string;
  icon: ReactNode;
  to?: string;
  children: ReactNode;
  minW?: number;
}) {
  const header = (
    <div className="flex items-center gap-2 text-muted-foreground">
      {icon}
      <h2 className="text-xs font-semibold uppercase tracking-wide">{title}</h2>
    </div>
  );
  return (
    <Card style={{ minWidth: minW }} className="flex-1">
      <CardContent className="flex flex-col gap-2 py-3">
        {to ? (
          <Link to={to} className="transition-colors hover:text-foreground">{header}</Link>
        ) : (
          header
        )}
        {children}
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-foreground">{value ?? '—'}</span>
    </div>
  );
}

function CardSpinner() {
  return (
    <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
      <Spinner size="xs" /> Loading…
    </div>
  );
}

// ---- cards --------------------------------------------------------------

function ServerCard() {
  const { data, isLoading } = useHealth();
  const seats = useSeats();
  const p4d = data?.p4d;
  const u = seats.data?.users;
  return (
    <StatCard
      title="Perforce server"
      icon={p4d?.reachable ? <IconCircleCheck size={14} className="text-green-500" /> : <IconServer size={14} />}
    >
      {isLoading ? (
        <CardSpinner />
      ) : !p4d ? (
        <span className="text-xs text-muted-foreground">unavailable</span>
      ) : !p4d.reachable ? (
        <Badge tone="red">unreachable</Badge>
      ) : (
        <>
          <Badge tone="green">reachable</Badge>
          <Row label="Version" value={p4d.serverVersion} />
          <Row label="Address" value={p4d.serverAddress} />
          {u && (
            <Row
              label="Seats"
              value={
                <Badge tone={seatTone(u.count, u.limit)}>
                  {u.count ?? '?'} / {u.limit ?? '?'}
                </Badge>
              }
            />
          )}
        </>
      )}
    </StatCard>
  );
}

function RenderPipelineCard() {
  const { data, isLoading } = useJobs(true);
  return (
    <StatCard title="Render pipeline" icon={<IconStack size={14} />} to="/admin">
      {isLoading || !data ? (
        <CardSpinner />
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            <Badge tone="neutral">{data.counts.queued} queued</Badge>
            <Badge tone="blue">{data.counts.running} running</Badge>
            {data.counts.failed > 0 && <Badge tone="red">{data.counts.failed} failed</Badge>}
          </div>
          <Row label="Done / hr" value={data.completedLastHour.toLocaleString()} />
          {data.staleCount > 0 && (
            <Row label="Pending re-render" value={<span className="text-yellow-400">{data.staleCount.toLocaleString()}</span>} />
          )}
          {data.hourlyDone.some((n) => n > 0) && (
            <div className="mt-1">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Renders / hr · 24h</span>
              <Sparkline values={data.hourlyDone} width={220} height={32} fillBelow ariaLabel="renders per hour, last 24 hours" />
            </div>
          )}
        </>
      )}
    </StatCard>
  );
}

function StorageCard() {
  const { data, isLoading } = useStorage(true);
  const pct = diskPct(data);
  const tone = pct === null ? 'neutral' : pct >= DISK_CRIT_PCT ? 'red' : pct >= DISK_WARN_PCT ? 'yellow' : 'green';
  return (
    <StatCard title="Storage" icon={<IconDatabase size={14} />} to="/admin">
      {isLoading || !data ? (
        <CardSpinner />
      ) : (
        <>
          {pct !== null && (
            <div className="flex items-center gap-2">
              <Badge tone={tone}>{pct.toFixed(0)}% disk used</Badge>
              <span className="text-[11px] text-muted-foreground">
                {formatBytes(data.filesystem.freeBytes)} free
              </span>
            </div>
          )}
          <Row label="DAM cache" value={formatBytes(data.damCache.bytes)} />
          <Row label="p4d data" value={formatBytes(data.perforce.p4dDataBytes)} />
        </>
      )}
    </StatCard>
  );
}

function BackupCard() {
  const { data, isLoading } = useBackup(true);
  const snap = data?.snapshot;
  return (
    <StatCard title="Backup" icon={<IconClock size={14} />} to="/admin/backups">
      {isLoading ? (
        <CardSpinner />
      ) : !snap ? (
        <span className="text-xs text-muted-foreground">{data?.message ?? 'no snapshot yet'}</span>
      ) : (
        <>
          <Badge tone={statusTone(snap.lastRunStatus)}>{snap.lastRunStatus ?? 'unknown'}</Badge>
          <Row label="Last run" value={relativeTime(snap.lastRunEndedAt ?? snap.takenAt)} />
          <Row label="Mirror size" value={formatBytes(snap.totalBytes)} />
        </>
      )}
    </StatCard>
  );
}

function RecentActivityCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['changes', { limit: 8 }],
    queryFn: () => api.changes({ limit: 8 }),
    refetchInterval: 60_000,
  });
  return (
    <StatCard title="Recent activity" icon={<IconGitCommit size={14} />} to="/changes" minW={360}>
      {isLoading || !data ? (
        <CardSpinner />
      ) : data.changes.length === 0 ? (
        <span className="text-xs text-muted-foreground">No submitted changes.</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {data.changes.map((c: ChangeRow) => (
            <li key={c.change} className="flex items-baseline gap-2 text-xs">
              <Link to={`/changes/${c.change}`} className="font-mono text-theia-400 hover:underline">
                @{c.change}
              </Link>
              <span className="min-w-0 flex-1 truncate text-foreground" title={c.description}>
                {firstLine(c.description)}
              </span>
              <span className="shrink-0 text-muted-foreground">{c.user ?? '—'}</span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{relativeTime(c.time)}</span>
            </li>
          ))}
        </ul>
      )}
    </StatCard>
  );
}

function DepotOverviewCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['depot.list', '//'],
    queryFn: () => api.depotList('//'),
    refetchInterval: 5 * 60_000,
  });
  const depots = (data as DepotListResponse | undefined)?.dirs ?? [];
  return (
    <StatCard title="Depots" icon={<IconFolder size={14} />} to="/depot" minW={360}>
      {isLoading ? (
        <CardSpinner />
      ) : depots.length === 0 ? (
        <span className="text-xs text-muted-foreground">No depots.</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {depots.map((d) => (
            <li key={d.path} className="flex items-baseline gap-2 text-xs">
              <Link
                to={`/depot${d.path.replace(/^\/\//, '/')}`}
                className="min-w-0 flex-1 truncate font-mono text-theia-400 hover:underline"
              >
                {d.path.replace(/^\/\//, '')}
              </Link>
              <span className="shrink-0 text-muted-foreground">
                {(d.depotInfo?.fileCount ?? 0).toLocaleString()} files
              </span>
              <span className="shrink-0 font-mono text-muted-foreground">
                {d.depotInfo?.fileSize != null ? formatBytes(d.depotInfo.fileSize) : '—'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </StatCard>
  );
}

function ContentHealthCard() {
  const { data, isLoading } = useContentHealth();
  return (
    <StatCard title="Content health" icon={<IconActivity size={14} />} to="/depot">
      {isLoading || !data ? (
        <CardSpinner />
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            <Badge tone={data.brokenRefs > 0 ? 'red' : 'green'}>{data.brokenRefs} broken refs</Badge>
            <Badge tone={data.overBudget.total > 0 ? 'yellow' : 'neutral'}>
              {data.overBudget.total} over budget
            </Badge>
          </div>
          <Row label="Indexed assets" value={data.indexedAssets.toLocaleString()} />
        </>
      )}
    </StatCard>
  );
}

function LocksCard() {
  const { data, isLoading } = useLocks();
  const locks = data?.locks ?? [];
  return (
    <StatCard title="Exclusive locks" icon={<IconLock size={14} />} minW={360}>
      {isLoading ? (
        <CardSpinner />
      ) : locks.length === 0 ? (
        <span className="text-xs text-muted-foreground">No exclusive checkouts.</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {locks.map((l) => (
            <li key={`${l.depotPath}#${l.rev}`} className="flex items-baseline gap-2 text-xs">
              <span className="min-w-0 flex-1 truncate font-mono text-foreground" title={l.depotPath}>
                {l.depotPath.replace(/^.*\//, '')}
              </span>
              <span className="shrink-0 text-muted-foreground">{l.action ?? '—'}</span>
              <span className="shrink-0 text-muted-foreground">{l.user ?? '—'}</span>
            </li>
          ))}
        </ul>
      )}
    </StatCard>
  );
}

function AdminActionsCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin.actions', 8],
    queryFn: () => api.adminActions(8),
    refetchInterval: 60_000,
  });
  const actions: AdminAction[] = data?.actions ?? [];
  return (
    <StatCard title="Admin actions" icon={<IconHistory size={14} />} to="/admin" minW={360}>
      {isLoading ? (
        <CardSpinner />
      ) : actions.length === 0 ? (
        <span className="text-xs text-muted-foreground">No recent admin actions.</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {actions.map((a) => (
            <li key={a.id} className="flex items-baseline gap-2 text-xs">
              <span className="min-w-0 flex-1 truncate text-foreground" title={a.actionType}>
                {a.actionType}
              </span>
              <Badge tone={actionTone(a.status)}>{a.status}</Badge>
              <span className="shrink-0 text-muted-foreground">{a.actor}</span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{relativeTime(a.startedAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </StatCard>
  );
}

function actionTone(status: AdminAction['status']): 'green' | 'red' | 'yellow' | 'blue' {
  return status === 'success' ? 'green' : status === 'failed' ? 'red' : status === 'partial' ? 'yellow' : 'blue';
}

function firstLine(s: string): string {
  const line = s.split('\n', 1)[0] ?? '';
  return line.length > 80 ? line.slice(0, 79) + '…' : line;
}
